import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Moon, Zap, CheckSquare, Target, Smile, Dumbbell, DollarSign, Star,
  BookOpen, Flag, Utensils, Wind, Crosshair, Heart, Users, GraduationCap, Flame,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";

const ICONS: Record<string, React.ElementType> = {
  Moon, Zap, CheckSquare, Target, Smile, Dumbbell, DollarSign,
  BookOpen, Flag, Utensils, Wind, Crosshair, Heart, Users, GraduationCap, Flame,
};

export interface Contributor {
  name: string;
  value: number;
  icon: string;
  route: string;
  hasData?: boolean;
  rawKey?: string;
}

interface MirrorScoreProps {
  score: number;
  contributors: Contributor[];
  mirrorMessage: string;
  loading?: boolean;
}

const getScoreColor = (s: number) =>
  s >= 85 ? "hsl(187 72% 50%)" : s >= 70 ? "hsl(142 71% 45%)" : s >= 40 ? "hsl(40 90% 60%)" : "hsl(0 72% 51%)";

const getBarColor = (v: number) =>
  v >= 60 ? "hsl(142 71% 45%)" : v >= 40 ? "hsl(40 90% 60%)" : "hsl(0 72% 51%)";

const ContributorRow = ({
  c,
  i,
  mounted,
  navigate,
}: {
  c: Contributor;
  i: number;
  mounted: boolean;
  navigate: ReturnType<typeof useNavigate>;
}) => {
  const Icon = ICONS[c.icon] || Zap;
  const noData = c.hasData === false;
  const { t } = useLanguage();

  return (
    <button
      key={c.name}
      onClick={() => navigate(c.route)}
      className="w-full flex items-center gap-2.5 group"
      style={{ animation: `fadeInUp 0.5s ease-out ${0.6 + i * 0.08}s both` }}
    >
      <Icon
        size={13}
        className={`shrink-0 transition-colors ${
          noData
            ? "text-muted-foreground/30"
            : "text-muted-foreground group-hover:text-primary/70"
        }`}
      />
      <div className="flex-1">
        <div className="flex justify-between items-baseline mb-1">
          <span
            className={`font-display text-[11px] font-medium transition-colors ${
              noData
                ? "text-foreground/30"
                : "text-foreground/70 group-hover:text-foreground"
            }`}
          >
            {c.name}
          </span>
          {noData ? (
            <span className="text-[10px] font-display text-muted-foreground/30">–</span>
          ) : (
            <span className="text-[10px] font-display font-semibold" style={{ color: getBarColor(c.value) }}>
              {c.value}%
            </span>
          )}
        </div>
        <div className="w-full h-[5px] bg-muted/15 rounded-full overflow-hidden relative">
          {noData ? (
            <div className="h-full rounded-full bg-muted/20" style={{ width: "40%" }} />
          ) : (
            <motion.div
              className="h-full rounded-full absolute inset-y-0 left-0"
              style={{
                background: `linear-gradient(90deg, ${getBarColor(c.value)}99, ${getBarColor(c.value)})`,
                boxShadow: `0 0 6px ${getBarColor(c.value)}60`,
              }}
              initial={{ width: "0%" }}
              animate={{ width: mounted ? `${c.value}%` : "0%" }}
              transition={{ duration: 0.9, delay: 0.6 + i * 0.1, ease: [0.4, 0, 0.2, 1] }}
            />
          )}
        </div>
        {noData && (
          <p className="text-[9px] font-display text-muted-foreground/30 mt-0.5 tracking-wide">
            {t("score.noData")}
          </p>
        )}
      </div>
    </button>
  );
};

const MirrorScore = ({ score, contributors, mirrorMessage, loading }: MirrorScoreProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [displayScore, setDisplayScore] = useState(0);
  const [mounted, setMounted] = useState(false);
  const startTimeRef = useRef<number | null>(null);

  const animateCount = useCallback((timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = timestamp - startTimeRef.current;
    const progress = Math.min(elapsed / 1500, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    setDisplayScore(Math.round(eased * score));
    if (progress < 1) requestAnimationFrame(animateCount);
  }, [score]);

  useEffect(() => {
    if (loading) return;
    const t1 = setTimeout(() => setMounted(true), 200);
    const t2 = setTimeout(() => requestAnimationFrame(animateCount), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [animateCount, loading]);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="flex flex-col items-center gap-3">
          <div className="w-[180px] h-[180px] rounded-full bg-muted/10" />
          <div className="w-24 h-3 rounded-full bg-muted/15" />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <div className="w-3 h-3 rounded-full bg-muted/15" />
            <div className="flex-1 space-y-1.5">
              <div className="w-full h-[5px] rounded-full bg-muted/15" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const r2 = 68;
  const r3 = 58;
  const c2 = 2 * Math.PI * r2;
  const offset2 = c2 * (1 - (mounted ? score / 100 : 0));
  const color = getScoreColor(score);

  const statusKey =
    score < 40 ? "score.status.attention" : score < 70 ? "score.status.building" : score < 85 ? "score.status.ontrack" : "score.status.peak";

  const coreContributors = contributors.filter((c) => c.hasData !== false);
  const newContributors = contributors.filter((c) => c.hasData === false);

  return (
    <div className="space-y-4" style={{ filter: "drop-shadow(0 0 30px hsl(187 72% 50% / 0.08))" }}>
      {/* Gauge */}
      <div className="flex flex-col items-center">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase text-primary/55 mb-3">
          {t("score.title")}
        </p>
        <div className="relative w-[180px] h-[180px]">
          <svg width="180" height="180" viewBox="0 0 180 180">
            <defs>
              <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.9" />
                <stop offset="100%" stopColor={score >= 70 ? "hsl(187 72% 70%)" : "hsl(40 90% 70%)"} />
              </linearGradient>
              <filter id="scoreGlow">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Outer decorative ring */}
            <circle
              cx="90" cy="90" r="82"
              fill="none"
              stroke="hsl(187 72% 50% / 0.08)"
              strokeWidth="1"
              strokeDasharray="1 10"
              className="mirror-score-rotate"
            />

            {/* Counter-rotating ring */}
            <circle
              cx="90" cy="90" r="76"
              fill="none"
              stroke="hsl(187 72% 50% / 0.05)"
              strokeWidth="1"
              strokeDasharray="3 14"
              style={{ animation: "spin 18s linear infinite reverse" }}
            />

            {/* Track background */}
            <circle
              cx="90" cy="90" r={r2}
              fill="none"
              stroke="hsl(222 18% 12%)"
              strokeWidth="13"
              strokeLinecap="round"
            />

            {/* Score arc with glow */}
            <circle
              cx="90" cy="90" r={r2}
              fill="none"
              stroke="url(#scoreGrad)"
              strokeWidth="13"
              strokeLinecap="round"
              strokeDasharray={c2}
              strokeDashoffset={offset2}
              transform="rotate(-90 90 90)"
              filter="url(#scoreGlow)"
              style={{
                transition: "stroke-dashoffset 1.8s cubic-bezier(0.4,0,0.2,1)",
              }}
            />

            {/* Inner subtle ring */}
            <circle
              cx="90" cy="90" r={r3}
              fill="none"
              stroke={`${color}18`}
              strokeWidth="1"
            />

            {/* Tick marks at key thresholds */}
            {[0, 25, 50, 75, 100].map((pct) => {
              const angle = (pct / 100) * 360 - 90;
              const rad = (angle * Math.PI) / 180;
              const x1 = 90 + (r2 - 8) * Math.cos(rad);
              const y1 = 90 + (r2 - 8) * Math.sin(rad);
              const x2 = 90 + (r2 + 8) * Math.cos(rad);
              const y2 = 90 + (r2 + 8) * Math.sin(rad);
              return (
                <line
                  key={pct}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="hsl(187 72% 50% / 0.20)"
                  strokeWidth="1"
                />
              );
            })}
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span
              className="font-display text-[52px] font-bold leading-none tracking-tight"
              style={{ color, textShadow: `0 0 20px ${color}40` }}
            >
              {displayScore}
            </span>
            <span className="text-muted-foreground/50 text-[10px] font-display mt-0.5 tracking-wider">
              / 100
            </span>
            <span
              className="text-[9px] font-display tracking-[0.18em] uppercase mt-1.5"
              style={{ color: `${color}90` }}
            >
              {t(statusKey)}
            </span>
          </div>
        </div>
      </div>

      {/* Core contributors */}
      <div className="space-y-2.5">
        {coreContributors.map((c, i) => (
          <ContributorRow key={c.name} c={c} i={i} mounted={mounted} navigate={navigate} />
        ))}
      </div>

      {/* New areas separator + rows */}
      {newContributors.length > 0 && (
        <>
          <div className="flex items-center gap-2 pt-1">
            <div className="flex-1 h-[1px] bg-border/20" />
            <span className="text-[8px] font-display tracking-[0.25em] uppercase text-muted-foreground/35 px-1">
              {t("score.newAreas")}
            </span>
            <div className="flex-1 h-[1px] bg-border/20" />
          </div>
          <div className="space-y-2.5">
            {newContributors.map((c, i) => (
              <ContributorRow key={c.name} c={c} i={coreContributors.length + i} mounted={mounted} navigate={navigate} />
            ))}
          </div>
        </>
      )}

      {/* Mirror Message */}
      {mirrorMessage && mirrorMessage.trim() !== "" && (
        <div
          className="rounded-xl p-4 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, hsl(187 40% 8% / 0.8), hsl(200 35% 7% / 0.8))",
            border: "1px solid hsl(187 72% 50% / 0.12)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
          <div className="flex items-start gap-2.5">
            <Star size={13} className="text-primary/50 mt-0.5 shrink-0" />
            <p className="text-foreground/55 text-[12px] font-body leading-relaxed italic">
              {mirrorMessage}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MirrorScore;
